// Substrate-neutral canonical shape source. Generated from
// `fixtures/design-composition-shapes-v1.ttl` by `scripts/generate-shapes-content.ts`;
// a `*.ttl?raw` import here was Vite-only and threw at import time under Node/tsx.
import { designCompositionShapesV1Ttl as canonicalShapesTtl } from "./_generated/shapes-content"
// @ts-ignore -- n3 does not publish declarations compatible with this workspace.
import { Parser as N3Parser } from "n3"
// @ts-ignore -- rdf-ext does not publish declarations compatible with this workspace.
import rdf from "rdf-ext"
// @ts-ignore -- rdf-validate-shacl does not publish declarations compatible with this workspace.
import SHACLValidator from "rdf-validate-shacl"
import type { Quad } from "@rdfjs/types"

export type DesignCompositionQuad = Quad

export interface DuplicateDesignCompositionRegionIdIssue {
  code: "duplicate-region-id"
  recipeRef: string
  regionId: string
  regionRefs: string[]
}

export interface DesignCompositionShaclIssue {
  code: "shacl-conformance-failed"
  path: string
  message: string
  focusNode?: string
}

export interface DesignCompositionReleaseCardinalityIssue {
  code: "release-cardinality-invalid"
  releaseRefs: string[]
  message: string
}

export interface DesignCompositionRdfInputIssue {
  code: "rdf-input-invalid"
  path: string
  message: string
}

export type DesignCompositionAdmissionIssue =
  | DuplicateDesignCompositionRegionIdIssue
  | DesignCompositionShaclIssue
  | DesignCompositionReleaseCardinalityIssue
  | DesignCompositionRdfInputIssue

export interface DesignCompositionAdmissionResult {
  ok: boolean
  issues: DesignCompositionAdmissionIssue[]
}

interface DesignCompositionRegionIdUniquenessResult {
  ok: boolean
  issues: DuplicateDesignCompositionRegionIdIssue[]
}

const RDF_TYPE =
  "http://www.w3.org/1999/02/22-rdf-syntax-ns#type"
const DESIGN_COMPOSITION_NS =
  "https://cogo.xyz/ns/design-composition/v1#"
const DESIGN_COMPOSITION_RELEASE =
  `${DESIGN_COMPOSITION_NS}DesignCompositionRelease`
const COMPOSITION_RECIPE = `${DESIGN_COMPOSITION_NS}CompositionRecipe`
const HAS_REGION = `${DESIGN_COMPOSITION_NS}hasRegion`
const REGION_ID = `${DESIGN_COMPOSITION_NS}regionId`

function parseDataset(ttl: string) {
  const dataset = rdf.dataset()
  for (const quad of new N3Parser().parse(ttl)) dataset.add(quad)
  return dataset
}

/**
 * Narrow deterministic check for the uniqueness rule that SHACL Core cannot
 * express without SPARQL. This is intentionally not the package admission API.
 */
type RegionIdentityQuad = Pick<
  Quad,
  "subject" | "predicate" | "object"
>

function findDuplicateDesignCompositionRegionIds(
  quads: Iterable<RegionIdentityQuad>,
): DesignCompositionRegionIdUniquenessResult {
  const recipes = new Set<string>()
  const regionRefsByRecipe = new Map<string, Set<string>>()
  const regionIdsByRef = new Map<string, Set<string>>()

  for (const quad of quads) {
    if (
      quad.predicate.value === RDF_TYPE &&
      quad.object.value === COMPOSITION_RECIPE
    ) {
      recipes.add(quad.subject.value)
    }
    if (quad.predicate.value === HAS_REGION) {
      const refs =
        regionRefsByRecipe.get(quad.subject.value) ?? new Set<string>()
      refs.add(quad.object.value)
      regionRefsByRecipe.set(quad.subject.value, refs)
    }
    if (quad.predicate.value === REGION_ID) {
      const ids =
        regionIdsByRef.get(quad.subject.value) ?? new Set<string>()
      ids.add(quad.object.value)
      regionIdsByRef.set(quad.subject.value, ids)
    }
  }

  const issues: DuplicateDesignCompositionRegionIdIssue[] = []
  for (const recipeRef of [...recipes].sort()) {
    const refsByRegionId = new Map<string, Set<string>>()
    for (const regionRef of regionRefsByRecipe.get(recipeRef) ?? []) {
      for (const regionId of regionIdsByRef.get(regionRef) ?? []) {
        const refs = refsByRegionId.get(regionId) ?? new Set<string>()
        refs.add(regionRef)
        refsByRegionId.set(regionId, refs)
      }
    }
    for (const [regionId, regionRefs] of [
      ...refsByRegionId.entries(),
    ].sort(([left], [right]) => left.localeCompare(right))) {
      if (regionRefs.size < 2) continue
      issues.push({
        code: "duplicate-region-id",
        recipeRef,
        regionId,
        regionRefs: [...regionRefs].sort(),
      })
    }
  }

  return { ok: issues.length === 0, issues }
}

function shaclMessage(result: any): string {
  const messages = Array.isArray(result?.message)
    ? result.message
    : result?.message
      ? [result.message]
      : []
  const rendered = messages
    .map((candidate: any) => candidate?.value ?? String(candidate))
    .filter(Boolean)
  return rendered.join("; ") || "Design composition SHACL constraint failed."
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null
}

function hasRdfTermBase(
  value: unknown,
  allowedTermTypes: readonly string[],
): value is Record<string, unknown> {
  return (
    isRecord(value) &&
    typeof value.termType === "string" &&
    allowedTermTypes.includes(value.termType) &&
    typeof value.value === "string" &&
    typeof value.equals === "function"
  )
}

function isRdfJsQuad(value: unknown): value is Quad {
  if (
    !hasRdfTermBase(value, ["Quad"]) ||
    value.value !== ""
  ) {
    return false
  }
  const subject = value.subject
  const predicate = value.predicate
  const object = value.object
  const graph = value.graph

  const subjectValid =
    hasRdfTermBase(subject, ["NamedNode", "BlankNode", "Variable"]) ||
    isRdfJsQuad(subject)
  const predicateValid = hasRdfTermBase(predicate, [
    "NamedNode",
    "Variable",
  ])
  const objectValid =
    hasRdfTermBase(object, [
      "NamedNode",
      "BlankNode",
      "Literal",
      "Variable",
    ]) || isRdfJsQuad(object)
  const graphValid = hasRdfTermBase(graph, [
    "DefaultGraph",
    "NamedNode",
    "BlankNode",
    "Variable",
  ])

  if (!subjectValid || !predicateValid || !objectValid || !graphValid) {
    return false
  }
  if (
    isRecord(graph) &&
    graph.termType === "DefaultGraph" &&
    graph.value !== ""
  ) {
    return false
  }
  if (isRecord(object) && object.termType === "Literal") {
    return (
      typeof object.language === "string" &&
      hasRdfTermBase(object.datatype, ["NamedNode"])
    )
  }
  return true
}

function rdfInputFailure(
  path: string,
  message: string,
): DesignCompositionAdmissionResult {
  return {
    ok: false,
    issues: [{ code: "rdf-input-invalid", path, message }],
  }
}

/**
 * Sole public composition ontology admission gate. It always evaluates the
 * canonical SHACL shapes, requires exactly one release, and then applies the
 * deterministic recipe-region uniqueness rule.
 */
export async function admitDesignCompositionOntology(
  quads: Iterable<DesignCompositionQuad>,
): Promise<DesignCompositionAdmissionResult> {
  let materialized: DesignCompositionQuad[]
  try {
    if (
      quads === null ||
      quads === undefined ||
      typeof (quads as any)[Symbol.iterator] !== "function"
    ) {
      return rdfInputFailure(
        "quads",
        "Design composition RDF input must be an iterable of RDFJS Quads.",
      )
    }
    materialized = [...quads]
  } catch (error) {
    return rdfInputFailure(
      "quads",
      `Design composition RDF input could not be read: ${(error as Error).message}`,
    )
  }

  try {
    const invalidIndex = materialized.findIndex(
      (quad) => !isRdfJsQuad(quad),
    )
    if (invalidIndex >= 0) {
      return rdfInputFailure(
        `quads[${invalidIndex}]`,
        "Design composition RDF input must contain complete RDFJS Quad values.",
      )
    }
  } catch (error) {
    return rdfInputFailure(
      "quads",
      `Design composition RDF input validation failed: ${(error as Error).message}`,
    )
  }

  const dataset = rdf.dataset()
  try {
    for (const quad of materialized) dataset.add(quad)
  } catch (error) {
    return rdfInputFailure(
      "quads",
      `Design composition RDF input could not be loaded: ${(error as Error).message}`,
    )
  }

  const validator = new SHACLValidator(
    parseDataset(canonicalShapesTtl),
    { importGraph: async () => rdf.dataset() },
  )
  const report = await validator.validate(dataset)
  const shaclIssues: DesignCompositionShaclIssue[] = (
    report.results ?? []
  ).map((result: any) => ({
    code: "shacl-conformance-failed",
    path: result.path?.value ?? "",
    message: shaclMessage(result),
    ...(result.focusNode?.value
      ? { focusNode: result.focusNode.value }
      : {}),
  }))

  const releaseRefs = [
    ...new Set(
      materialized
        .filter(
          (quad) =>
            quad.predicate.value === RDF_TYPE &&
            quad.object.value === DESIGN_COMPOSITION_RELEASE,
        )
        .map((quad) => quad.subject.value),
    ),
  ].sort()
  const releaseIssues: DesignCompositionReleaseCardinalityIssue[] =
    releaseRefs.length === 1
      ? []
      : [
          {
            code: "release-cardinality-invalid",
            releaseRefs,
            message: `Exactly one design composition release is required; received ${releaseRefs.length}.`,
          },
        ]

  const uniqueness =
    findDuplicateDesignCompositionRegionIds(materialized)
  const issues: DesignCompositionAdmissionIssue[] = [
    ...shaclIssues,
    ...releaseIssues,
    ...uniqueness.issues,
  ]
  return { ok: issues.length === 0, issues }
}
